import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  CircularProgress,
  IconButton,
  InputAdornment,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ExtensionOutlinedIcon from "@mui/icons-material/ExtensionOutlined";
import {
  SA,
  SaPageHeader,
  saCardSx,
  saTableHeadSx,
} from "../../components/superadmin/saUi";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import { toast } from "react-toastify";
import ConfirmDialog from "../../components/ConfirmDialog";
import {
  FEATURE_DESCRIPTIONS,
  FeatureMap,
  featureLabel,
  getFeatureKeys,
  listOrganizations,
  OrganizationRow,
  updateOrganizationFeatures,
} from "../../services/superadminService";
import { getErrorMessage } from "../../utils/apiResponse";

const PAGE_SIZE = 25;

interface PendingDisable {
  organization: OrganizationRow;
  feature: string;
}

const Features: React.FC = () => {
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [organizations, setOrganizations] = useState<OrganizationRow[]>([]);
  const [featureKeys, setFeatureKeys] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [savingCell, setSavingCell] = useState<string | null>(null);
  const [pendingDisable, setPendingDisable] = useState<PendingDisable | null>(
    null
  );

  useEffect(() => {
    // Canonical key list and column order — read, never hardcoded.
    getFeatureKeys()
      .then(setFeatureKeys)
      .catch(() => setFeatureKeys([]));
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search.trim());
      setPage(1);
    }, 350);
    return () => clearTimeout(timer);
  }, [search]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const result = await listOrganizations({
        page,
        pageSize: PAGE_SIZE,
        search: debouncedSearch || undefined,
      });
      // Deleted organizations have nothing to toggle.
      setOrganizations(result.organizations.filter((org) => !org.isDeleted));
      setTotalPages(result.totalPages || 1);
    } catch {
      // The interceptor already surfaced the message.
    }
    setLoading(false);
  }, [page, debouncedSearch]);

  useEffect(() => {
    load();
  }, [load]);

  const applyFeatures = (organizationId: string, features: FeatureMap) => {
    setOrganizations((prev) =>
      prev.map((org) => (org.id === organizationId ? { ...org, features } : org))
    );
  };

  const commit = async (org: OrganizationRow, feature: string, enabled: boolean) => {
    const previous = org.features;
    const cellKey = `${org.id}:${feature}`;
    applyFeatures(org.id, { ...previous, [feature]: enabled }); // optimistic
    setSavingCell(cellKey);
    try {
      const result = await updateOrganizationFeatures(org.id, [
        { feature, enabled },
      ]);
      // The response carries the complete map — drive state from it.
      applyFeatures(org.id, result.features);
    } catch (err: any) {
      applyFeatures(org.id, previous); // roll back
      toast.error(getErrorMessage(err, "Could not update the feature."), {
        position: "top-right",
      });
    }
    setSavingCell(null);
  };

  const handleToggle = (
    org: OrganizationRow,
    feature: string,
    enabled: boolean
  ) => {
    if (!enabled) {
      setPendingDisable({ organization: org, feature });
      return;
    }
    commit(org, feature, true);
  };

  const columns = featureKeys.length
    ? featureKeys
    : Object.keys(organizations[0]?.features || {});

  return (
    <Box>
      <SaPageHeader
        title="Features"
        subtitle="Switch a module on or off per organization. Turning one off hides it; nothing is deleted."
        icon={<ExtensionOutlinedIcon />}
        tone="violet"
      />

      <Paper elevation={0} sx={{ ...saCardSx, overflow: "hidden" }}>
        <Box sx={{ p: 2, borderBottom: `1px solid ${SA.divider}` }}>
          <TextField
            size="small"
            placeholder="Search organizations"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ minWidth: 260 }}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              },
            }}
          />
        </Box>

        <TableContainer>
          <Table size="small" sx={{ minWidth: 640 }}>
            <TableHead sx={saTableHeadSx}>
              <TableRow>
                <TableCell>Organization</TableCell>
                {columns.map((key) => (
                  <TableCell key={key} align="center">
                    <Tooltip title={FEATURE_DESCRIPTIONS[key] || key}>
                      <span>{featureLabel(key)}</span>
                    </Tooltip>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && (
                <TableRow>
                  <TableCell colSpan={columns.length + 1} align="center" sx={{ py: 6 }}>
                    <CircularProgress size={26} />
                  </TableCell>
                </TableRow>
              )}

              {!loading && organizations.length === 0 && (
                <TableRow>
                  <TableCell colSpan={columns.length + 1} align="center" sx={{ py: 6 }}>
                    <Typography sx={{ color: SA.textMuted }}>
                      No organizations match this search.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}

              {!loading &&
                organizations.map((org) => (
                  <TableRow key={org.id} hover>
                    <TableCell>
                      <Typography sx={{ fontWeight: 600, fontSize: "0.92rem" }}>
                        {org.name}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "0.75rem",
                          color: SA.textMuted,
                        }}
                      >
                        {org.usage?.clients ?? 0} clients ·{" "}
                        {org.usage?.bookingsTotal ?? 0} bookings
                      </Typography>
                    </TableCell>
                    {columns.map((key) => (
                      <TableCell key={key} align="center">
                        <Switch
                          size="small"
                          checked={!!org.features?.[key]}
                          disabled={savingCell === `${org.id}:${key}`}
                          onChange={(e) =>
                            handleToggle(org, key, e.target.checked)
                          }
                          inputProps={{
                            "aria-label": `${featureLabel(key)} for ${org.name}`,
                          }}
                        />
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            gap: 1.5,
            p: 1.5,
            borderTop: `1px solid ${SA.divider}`,
          }}
        >
          <Typography sx={{ fontSize: "0.82rem" }}>
            Page {page} of {totalPages}
          </Typography>
          <IconButton
            size="small"
            disabled={page <= 1 || loading}
            onClick={() => setPage((prev) => Math.max(1, prev - 1))}
          >
            <ArrowBackIosNewIcon sx={{ fontSize: 14 }} />
          </IconButton>
          <IconButton
            size="small"
            disabled={page >= totalPages || loading}
            onClick={() => setPage((prev) => prev + 1)}
          >
            <ArrowForwardIosIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </Box>
      </Paper>

      <ConfirmDialog
        open={!!pendingDisable}
        title={
          pendingDisable
            ? `Hide ${featureLabel(pendingDisable.feature)}?`
            : "Hide feature?"
        }
        message={
          pendingDisable
            ? `${pendingDisable.organization.name} will lose access to ${featureLabel(
                pendingDisable.feature
              )} across the backoffice and the app. Nothing is deleted — their existing records stay, and switching it back on restores access exactly as it was.`
            : ""
        }
        confirmButtonText="Hide it"
        onClose={() => setPendingDisable(null)}
        onConfirm={() => {
          if (pendingDisable) {
            commit(pendingDisable.organization, pendingDisable.feature, false);
          }
          setPendingDisable(null);
        }}
      />
    </Box>
  );
};

export default Features;
